/** The asset page's Activity tab: the audit trail of the asset, newest first. */
import { useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronRight, X } from 'lucide-react';
import { useAssetAudit } from '../api/hooks';
import { When, dateTime } from '../lib/format';
import { ACTIVITY_FILTERS, actorLabel, groupActivity, type ActivityGroup, type ActivityKind, type AuditEvent } from '../lib/activity';

const ACTION_LABELS: Record<string, string> = {
  'asset.upload': 'Uploaded',
  'asset.placeholder_create': 'Created as a placeholder',
  'asset.metadata.update': 'Edited metadata',
  'processing.retry': 'Retried processing',
  'processing.succeeded': 'Processing succeeded',
  'processing.partial': 'Processing partially succeeded',
  'processing.failed': 'Processing failed',
  'processing.cancelled': 'Processing cancelled',
  'asset.dismiss': 'Dismissed',
  'asset.delete': 'Moved to the recycle bin',
  'asset.undelete': 'Restored from the recycle bin',
  // 1.2.2 / 1.2.3: versions.
  'asset.version.upload': 'Uploaded a new version',
  'asset.version.delete': 'Moved an earlier version to the recycle bin',
  'asset.version.undelete': 'Restored an earlier version from the recycle bin',
  'asset.version.purge': 'Deleted an earlier version permanently',
  'asset.version.make_current': 'Made an earlier version current again',
  'asset.access.update': 'Changed restricted access',
  'share.create': 'Made a share link',
  'share.revoke': 'Revoked a share link',
  'share.media': 'A dead share link was refused',
  'approval.request': 'Asked for approval',
  'approval.approved': 'Approved',
  'approval.rejected': 'Changes requested',
  'transfer.request': 'Requested a transfer',
  'asset.attachment.add': 'Added a file',
  'asset.attachment.remove': 'Removed a file',
  'automation.run': 'Started a workflow',
  'asset.watch_ingest': 'Ingested from a watch folder',
  'task.create': 'Made a task',
  'asset.category': 'Changed the class',
  'asset.relation.add': 'Related to another asset',
  'asset.relation.remove': 'Removed a relation',
  'asset.graphic_defaults': 'Changed the graphic’s defaults',
  'graphics.save': 'Saved the graphic',
  'subtitles.create': 'Added subtitles',
  'subtitles.import': 'Imported subtitles',
  'subtitles.delete': 'Deleted subtitles',
  'subtitles.programme_offset': 'Moved subtitles to the media’s timecode',
  'subtitles.transcribe_request': 'Asked for generated subtitles',
  'subtitles.transcribe': 'Generated subtitles',
  'subtitles.transcribe_cancel': 'Cancelled generating subtitles',
  'processing.regenerate': 'Regenerated previews',
  'asset.rendition': 'Made a rendition',
  'asset.export': 'Exported',
  'asset.restore': 'Asked for a restore from the archive',
  'asset.file.delete': 'Removed a file from storage',
  'asset.discover': 'Found on storage',
  'asset.purge': 'Deleted for good',
  'asset.live_start': 'Started a live recording',
  'asset.live_recorded': 'Recorded live',
  'transfer.succeeded': 'Transfer done',
  'transfer.failed': 'Transfer failed',
};

function details(e: AuditEvent): string {
  return [
    Array.isArray(e.detail.fields) ? ` · ${(e.detail.fields as string[]).length} field${(e.detail.fields as string[]).length > 1 ? 's' : ''}` : '',
    typeof e.detail.filename === 'string' && e.action.startsWith('asset.attachment') ? ` · ${e.detail.filename}` : '',
    typeof e.detail.workflow === 'string' ? ` · ${e.detail.workflow}` : '',
    typeof e.detail.storage === 'string' ? ` · stored in ${e.detail.storage}` : '',
    e.action === 'asset.version.make_current' ? ` · version ${String(e.detail.to)} instead of ${String(e.detail.from)}` : '',
  ].join('');
}

function Outcome({ e }: { e: AuditEvent }) {
  return <span className={`step-icon ${e.outcome === 'success' ? 'succeeded' : 'failed'}`}>{e.outcome === 'success' ? <Check size={12} strokeWidth={3} /> : <X size={12} strokeWidth={3} />}</span>;
}

function EventRow({ e, nested = false }: { e: AuditEvent; nested?: boolean }) {
  return (
    <div className={`step ${nested ? 'activity-nested' : ''}`} data-testid="activity-event" data-action={e.action}>
      <Outcome e={e} />
      <div className="step-main">
        <span className="step-title">{ACTION_LABELS[e.action] ?? e.action}</span>
        <span className="step-sub">{actorLabel(e)}{details(e)}</span>
      </div>
      <When iso={e.at} className="step-time" />
    </div>
  );
}

/** A run of the same action by the same person: "Edited metadata ×6", with the time span; opens on click. */
function GroupRow({ g }: { g: ActivityGroup }) {
  const [open, setOpen] = useState(false);
  const newest = g.events[0]!;
  const oldest = g.events[g.events.length - 1]!;
  return (
    <div className="activity-group" data-testid="activity-group" data-open={open}>
      <button type="button" className="step activity-group-head" onClick={() => setOpen(!open)} aria-expanded={open} data-testid="activity-group-toggle">
        <Outcome e={newest} />
        <div className="step-main">
          <span className="step-title">
            {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />} {ACTION_LABELS[newest.action] ?? newest.action} <span className="pill" data-testid="activity-group-count">×{g.events.length}</span>
          </span>
          <span className="step-sub">{actorLabel(newest)} · {spanLabel(oldest.at, newest.at)}</span>
        </div>
        <When iso={newest.at} className="step-time" />
      </button>
      {open && <div className="activity-group-body">{g.events.map((e) => <EventRow key={e.id} e={e} nested />)}</div>}
    </div>
  );
}

function spanLabel(fromIso: string, toIso: string): string {
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
  const from = dateTime(fromIso);
  if (ms < 60_000) return `within a minute, ${from}`;
  const minutes = Math.round(ms / 60_000);
  const over = minutes < 90 ? `${minutes} min` : minutes < 48 * 60 ? `${Math.round(minutes / 60)} h` : `${Math.round(minutes / 1440)} days`;
  return `over ${over}, from ${from}`;
}

/** The asset page's Activity tab (0.99.1): the newest 50 events, filters, Show older, and runs of the same event grouped. */
export function ActivityTab({ id }: { id: string }) {
  const [kind, setKind] = useState<ActivityKind>('all');
  const q = useAssetAudit(id, kind);
  const events = useMemo(() => (q.data?.pages ?? []).flatMap((p) => p.items), [q.data]);
  const groups = useMemo(() => groupActivity(events), [events]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} data-testid="activity-tab">
      <div role="group" aria-label="Show" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {ACTIVITY_FILTERS.map((f) => (
          <button key={f.kind} type="button" className={`chip ${kind === f.kind ? 'active' : ''}`} aria-pressed={kind === f.kind} onClick={() => setKind(f.kind)} data-testid={`activity-filter-${f.kind}`}>{f.label}</button>
        ))}
      </div>
      {q.isLoading ? <div className="skeleton" style={{ height: 120 }} />
        : events.length === 0 ? <p className="muted" style={{ margin: 0 }}>{kind === 'all' ? 'No activity recorded yet.' : 'Nothing of this kind recorded.'}</p>
        : (
          <div className="steps">
            {groups.map((g) => g.events.length > 1 ? <GroupRow key={g.key} g={g} /> : <EventRow key={g.key} e={g.events[0]!} />)}
          </div>
        )}
      {q.hasNextPage && (
        <button className="btn ghost sm" style={{ alignSelf: 'flex-start' }} disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()} data-testid="activity-older">
          {q.isFetchingNextPage ? 'Loading…' : 'Show older'}
        </button>
      )}
      {!q.hasNextPage && events.length > 0 && <span className="faint" style={{ fontSize: 12 }} data-testid="activity-end">That is the whole trail; PRISM never deletes it.</span>}
    </div>
  );
}
